import { useCallback, ChangeEvent, useState, useRef, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
  Box,
  Flex,
  Button,
  ButtonProps,
  Grid,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  FormLabel,
  ListItem,
  FormControl,
  useDisclosure,
} from '@chakra-ui/react';
import debounce from 'lodash.debounce';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { matchSorter } from 'match-sorter';
import { withSiteExtensionData } from '../lib/hoc';
import { useOnScreen, useSwrStale } from '../lib/hooks';
import { Link, Person, OfficeTeaser, Site, SiteLocale } from '../types';
import PersonTeaser from './PersonTeaser';
import { useAppdata, useBlog, useTranslation } from '../lib/hooks.context';
import OrderedList from './OrderedList';
import Loader from './Loader';

const Modal = dynamic(() => import('./Modal'));
const Office = dynamic(() => import('./OfficeTeaser'));
const Logo = dynamic(() => import('./Logo'));

interface Props {
  people?: Person[];
  isLoading?: boolean;
  officeIsLoading?: boolean;
}

// Controls the fields to be searched when filtering. Expand if needed
const personSearchFields: string[] = [
  'firstname',
  'surname',
  'fullname',
  'territory.*',
  'office.*.name',
  'extra_text',
  'roles',
  'site',
];

const defaultLimit = 32;
const step = defaultLimit;

const filterItemsToShow = (items: Props['people'] = [], value: string) => {
  // Adds fullname for better filtering results with full name
  const people = items.map((i) => ({
    fullname: `${i.firstname} ${i.surname}`,
    ...i,
  }));
  return matchSorter(people, value, {
    keys: personSearchFields,
    threshold: matchSorter.rankings.CONTAINS,
  });
};

// State always holds people and filtered is either a subset of that or false
interface State {
  limit: number;
  filtered: false | Props['people'];
  searchTerm?: string;
  prevSearchTerm?: string;
  personBlogId?: number;
  personSubsite?: Site;
  personOfficeId?: number;
}

/* Not 100% sure why, but you have to use the debounce like so. Probably the fn gets re-created if put inside render */
const throttledRequest = debounce((cb) => {
  cb();
}, 750);

const LoadMoreButton = ({
  showMore,
  ...props
}: { showMore: () => void } & ButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const isVisible = useOnScreen(ref, '-10px');
  useEffect(() => {
    if (isVisible) {
      showMore();
    }
  }, [isVisible]);
  return (
    <Flex justifyContent="center">
      <Button onClick={showMore} variant="solidGreen" ref={ref} {...props} />
    </Flex>
  );
};

const PeopleSearch = ({ people }: Props) => {
  const t = useTranslation();
  const [
    {
      limit,
      filtered,
      searchTerm,
      prevSearchTerm,
      personBlogId,
      personSubsite,
      personOfficeId,
    },
    setState,
  ] = useState<State>({
    limit: defaultLimit,
    filtered: false,
  });

  const {
    locale: routerLocale,
    query: { sites: excludeSites },
  } = useRouter();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const subsite = useBlog(false);
  const { allSites } = useAppdata();

  const locale = routerLocale as SiteLocale;

  const { data: foundPeople } = useSwrStale<Person[]>(
    !subsite && searchTerm
      ? `/entities/people?sites=all&s=${searchTerm}${
          locale ? `&lang=${locale}` : ''
        }${excludeSites ? `&exclude=${excludeSites}` : ''}`
      : null,
  );

  const definePersonLocale = (currentLocale: SiteLocale): SiteLocale => {
    if (!currentLocale) {
      return 'fi';
    }
    return personSubsite?.languages &&
      personSubsite?.languages.includes(currentLocale)
      ? currentLocale
      : 'fi';
  };

  const initialPeople = subsite ? people : [];

  const isLoading = !!(
    searchTerm &&
    searchTerm !== prevSearchTerm &&
    typeof foundPeople === 'undefined'
  );
  const allFiltered =
    // eslint-disable-next-line
    filtered
      ? searchTerm
        ? filterItemsToShow([...filtered, ...(foundPeople || [])], searchTerm)
        : [...filtered, ...(foundPeople || [])]
      : searchTerm // empty results is fine if they were searched.
      ? foundPeople
      : null;

  // Gets office by id
  const { data: officeDetails } = useSwrStale<OfficeTeaser[]>(
    // eslint-disable-next-line no-nested-ternary
    subsite
      ? personBlogId
        ? `/entities/office?blog_id=${personBlogId}&office_id=${personOfficeId}&lang=${locale}`
        : null
      : personBlogId
      ? `/entities/office?blog_id=${personBlogId}${
          locale ? `&lang=${definePersonLocale(locale)}` : ''
        }${personOfficeId && `&office_id=${personOfficeId}`}`
      : null,
  );

  const officeIsLoading = !officeDetails;

  const showOfficeDetails = (blogId: number, officeId: number) => {
    const filteredSubsite = allSites.filter((s) => s.blog_id === blogId);
    setState((s) => ({
      ...s,
      personBlogId: blogId,
      personSubsite: filteredSubsite[0],
      personOfficeId: officeId,
    }));
    onOpen();
  };

  const showMore = () => {
    setState((s) => ({
      ...s,
      limit: s.limit + step,
    }));
  };

  const onReset = () => {
    setState((s) => ({
      ...s,
      searchTerm: '',
      filtered: false,
    }));
  };

  const onSearch = useCallback(
    (searchBy: string) =>
      throttledRequest(() => {
        if (searchBy.trim().length > 2) {
          setState((s) => ({
            ...s,
            prevSearchTerm: s.searchTerm,
            searchTerm: searchBy.trim(),
          }));
        } else {
          onReset();
        }
      }),
    [],
  );

  const onFilter = debounce((searchBy: string) => {
    if (searchBy.trim().length > 2) {
      setState((s) => ({
        ...s,
        filtered: filterItemsToShow(people, searchBy.trim()),
      }));
    } else {
      onReset();
    }
  });

  const onChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    onFilter(value);

    // Subsites only filter the content
    if (!subsite) {
      onSearch(value);
    }
  };

  const createSubsiteLink = (site: Site): Link | null => {
    // No link shown if it's the current subsite
    if (!site || site.name === subsite) {
      return null;
    }
    const subsiteName = site?.blogname.replace('Valtakunnallinen', '');
    const personLocale = definePersonLocale(locale);
    const link = {
      title: `${t('go_to_site', {
        prefix: personLocale === 'sv' ? 'Svf' : 'MHY',
        subsite: subsiteName,
      })}`,
      url: `/${site?.name || ''}`,
      locale: personLocale,
    };
    return link;
  };

  return (
    <Box>
      <Flex
        as="header"
        mb={[8, 10, 14]}
        justifyContent="space-between"
        gridGap="4"
        flexWrap="wrap"
        sx={{ '> *': { width: 'auto' } }}
      >
        <FormControl size="lg">
          <FormLabel htmlFor="site-search">
            {t('people_search_placeholder')}
          </FormLabel>
          <InputGroup>
            <InputLeftElement pointerEvents="none" color="highlightColor">
              <MagnifyingGlass size="18" />
            </InputLeftElement>
            <Input
              variant="search"
              id="site-search"
              maxWidth={370}
              placeholder={t('search_keyword')}
              onChange={onChange}
              pl="10"
              pr="8"
            />
            {!isLoading ? null : (
              <InputRightElement>
                <Button as="span" variant="transparent" isLoading />
              </InputRightElement>
            )}
          </InputGroup>
        </FormControl>
      </Flex>
      <Grid
        aria-live="polite"
        templateColumns="repeat(auto-fit, minmax(275px, 1fr))"
        gridColumnGap={['6', '10']}
        gridRowGap={['6', '10', null, '16']}
        mb="10"
        as={OrderedList}
      >
        {(allFiltered || initialPeople)?.slice(0, limit).map((person) => (
          <PersonTeaser
            showOfficeDetails={showOfficeDetails}
            key={`${person.id}-${person?.surname?.replace(/\s/g, '-')}`}
            as={ListItem}
            {...person}
          />
        ))}
      </Grid>
      {allFiltered && !allFiltered?.length ? (
        <>
          {isLoading ? (
            <Loader size="md" speed=".666s" m="1rem auto 1rem 2rem" />
          ) : (
            <Text>{t('people_no_items')}</Text>
          )}
        </>
      ) : null}
      {people && people?.length > limit ? (
        <LoadMoreButton showMore={showMore}>{t('show_more')}</LoadMoreButton>
      ) : null}
      <Modal isOpen={isOpen} onClose={onClose}>
        {officeIsLoading ? (
          <Flex flex="1" height="100%" alignItems="center">
            <Loader size="md" speed=".666s" m="1rem auto 1rem auto" />
          </Flex>
        ) : (
          <Box py={5}>
            <Box mb={3}>
              <Logo
                subsite={personSubsite?.name}
                overrideLocale={locale ? definePersonLocale(locale) : 'fi'}
              />
            </Box>
            {officeDetails && officeDetails[0] ? (
              <Office
                {...officeDetails[0]}
                all_link={
                  personSubsite ? createSubsiteLink(personSubsite) : null
                }
              />
            ) : null}
          </Box>
        )}
      </Modal>
    </Box>
  );
};

export default withSiteExtensionData<Pick<Props, 'people'>>(PeopleSearch, {
  param: 'sites',
  dataKey: 'people',
});
